import useAuth from "../../../Hooks/useAuth";
import { useState, useEffect } from "react";
import ReportLayout from "../../../Layout/Reports/ReportLayout";
import ReportFilterCard from "../../../Components/Reports/ReportFilterCard";
import ReportLoadingState from "../../../Components/Reports/ReportLoadingState";
import ReportErrorState from "../../../Components/Reports/ReportErrorState";
import ReportEmptyState from "../../../Components/Reports/ReportEmptyState";
import ReportPdfPreview, {
  downloadPdfFromBase64,
} from "../../../Components/Reports/ReportPdfPreview";
import { FaFilePdf, FaDownload } from "react-icons/fa";
import { useLazyGetNewFirmsBetweenDatesReportLazyQuery } from "../../../store/storageRoles/reportsApi";
import moment from "moment-timezone";
import { buildExcelConfig } from "../../../Components/Reports/reportExcelDefaults";
import { getMonthStart } from "../../../utils/reportDateDefaults";
import { parentFirmLabel } from "../../../utils/firmTypeStyles";

export default function ReportNewFirmsBetweenDatesPage() {
  const { auth } = useAuth();
  const token = auth.accessToken;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const getEndOfDay = (date = new Date()) => {
    const todayEnd = moment(date).tz(tz).endOf("day"); // Türkiye saatine göre günün sonu
    return todayEnd.toDate();
  };

  // Başlangıç varsayılanı ayın ilk günü (Workspace #154)
  const [startDate, setStartDate] = useState(() => getMonthStart(tz));
  const [endDate, setEndDate] = useState(getEndOfDay());
  const [parsedData, setParsedData] = useState([]);

  const [getNewFirmsReport, { data: newFirmsReport, isLoading, isFetching, error }] =
    useLazyGetNewFirmsBetweenDatesReportLazyQuery();

  // RTK Query'de `isLoading` YALNIZCA ilk yüklemede true olur; aynı sayfada
  // "Raporu Getir"e tekrar basınca istek gitse bile loading görünmüyordu.
  // Bekleme durumu için `isFetching` de hesaba katılır.
  const isBusy = isLoading || isFetching;

  useEffect(() => {
    if (newFirmsReport && newFirmsReport.data) {
      setParsedData(newFirmsReport.data);
    }
  }, [newFirmsReport]);

  const handleSearch = () => {
    if (!startDate || !endDate) {
      return;
    }
    getNewFirmsReport({
      token,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      timeZone: tz,
    });
  };

  const handleClearFilters = () => {
    setStartDate(getMonthStart(tz));
    setEndDate(getEndOfDay());
    setParsedData([]);
  };

  // Excel verilerini hazırlama
  const formatExcelData = (data) => {
    if (!data) return [];
    return data.map((item) => ({
      "Firma Adı": item.firmName || "-",
      "Bayser No": item.bayserNo || "-",
      "Ana Firma": parentFirmLabel(item.mainFirm, item.mainFirmType),
      Şehir: item.city || "-",
      İlçe: item.town || "-",
      Telefon: item.tel || "-",
      "E-posta": item.email || "-",
      "Oluşturulma Tarihi": item.createdInfo
        ? moment(item.createdInfo).tz(tz).format("DD/MM/YYYY HH:mm")
        : "-",
    }));
  };

  // Excel konfigürasyonu
  const excelConfig = buildExcelConfig({
    sheetName: "Katılan Firmalar Raporu",
    widths: [25, 16, 20, 15, 15, 16, 28, 20],
    numericColumns: [],
    columnMap: {
      firmName: 0,
      bayserNo: 1,
      mainFirm: 2,
      city: 3,
      town: 4,
      tel: 5,
      email: 6,
      createdInfo: 7,
    },
  });

  // Rapor sunucuda PDF olarak üretilir (sözleşme firm-new-report 1.0);
  // Excel data'dan.
  const pdfBase64 = newFirmsReport?.pdfBase64;

  const headerActions = pdfBase64 ? (
    <button
      onClick={() => downloadPdfFromBase64(pdfBase64, newFirmsReport?.fileName)}
      title="PDF İndir"
      className="flex items-center gap-1.5 px-2.5 py-2 sm:px-3.5 bg-red-600 text-white rounded-lg shadow-sm hover:bg-red-700 active:bg-red-800 transition-colors text-xs font-SemiBold"
    >
      <FaFilePdf className="w-4 h-4" />
      <FaDownload className="w-3 h-3" />
      <span className="hidden sm:inline">PDF İndir</span>
    </button>
  ) : null;

  return (
    <ReportLayout
      title="Müşteri Eklenenler Raporu"
      headerActions={headerActions}
      fileName={`Aramizda_Katilan_Firmalar_${new Date()
        .toLocaleDateString("tr-TR")
        .replace(/\./g, "_")}`}
      excelData={formatExcelData(parsedData)}
      excelConfig={excelConfig}
      sidebar={
        <ReportFilterCard
          title="Filtre Seçenekleri"
          description="Rapor için tarih aralığını seçin"
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={isBusy}
          searchButtonText="Raporu Getir"
          clearButtonText="Temizle"
        />
      }
    >
      {isBusy && (
        <ReportLoadingState
          title="Firma Raporu Hazırlanıyor"
          description="Veriler yükleniyor, lütfen bekleyin..."
          progressText="Veriler işleniyor..."
          skeletonRows={5}
          skeletonColumns={6}
        />
      )}
      {error && (
        <ReportErrorState
          title="Hata Oluştu"
          message={error?.data?.message || error.message || "Rapor yüklenirken bir hata oluştu."}
          icon="warning"
        />
      )}
      {!isBusy && !error && pdfBase64 && (
        <ReportPdfPreview
          pdfBase64={pdfBase64}
          title={`Müşteri Eklenenler Raporu — ${parsedData.length} firma`}
        />
      )}
      {!isBusy && !error && !pdfBase64 && (
        <ReportEmptyState
          title="Rapor Hazır Değil"
          message="Tarih aralığını seçip Raporu Getir'e basın."
          icon="document"
        />
      )}
    </ReportLayout>
  );
}
